import { Component, computed, effect, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { ProMessagingService } from '../pro-messaging.service';
import { Subscription } from 'rxjs';
import { AuthService } from '../../core/security/auth.service';
import { MessagingGroupModel } from '../../api/models/messaging-group-model';
import { ProPhoneNumberGroupV2Datasource } from './pro-phone-number-group-v2.datasource';
import { MatAlertDialogComponent } from '../../dialogs/mat-alert/mat-alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ProPhoneNumberGroupItemV2Component } from '../phone-number-group-item/pro-phone-number-group-item-v2.component';
import { FlexModule } from '@angular/flex-layout';
import { MatDateRangeComponent } from '../../dialogs/mat-date-range/mat-date-range.component';

@Component({
  selector: 'app-pro-phone-number-group-v2',
  template: `
    <div class="thread-sort">
      <div fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="5px">
        <app-mat-date-range
          (onDateRangeSelected)="onDateRangeSelected($event)"
          [selectedDateRangeService]="messagingService.dateRangeService"
        ></app-mat-date-range>

        <mat-button-toggle-group
          aria-label="Sort Type"
          name="sortType"
          hideSingleSelectionIndicator
          [value]="sortType()"
          (change)="sortChanged($event.value)"
        >
          <mat-button-toggle matTooltip="Sort by date" value="sortByDate">
            <mat-icon>sort</mat-icon>
          </mat-button-toggle>
          <mat-button-toggle matTooltip="Sort by unread and date" value="sortByDateAndUnread">
            <mat-icon>segment</mat-icon>
          </mat-button-toggle>
        </mat-button-toggle-group>
      </div>
    </div>
    <mat-selection-list class="thread-list" [multiple]="false" [hideSingleSelectionIndicator]="true">
      <cdk-virtual-scroll-viewport itemSize="99" class="thread-viewport">
        <mat-list-option
          *cdkVirtualFor="let item of dataSource"
          [selected]="item?.threadKey === selectedTheadKey()"
          (click)="onRowClick($event, item)"
        >
          <app-pro-phone-number-group-item-v2
            [dataItem]="item"
            [searchText]="searchText()"
            [wabaConvExp]="item?.wabaConvExp"
            [hasContactsModule]="hasContactsModule()"
            [hasContactsEditRight]="hasContactsEditRight()"
            [pinUpdateInProgress]="pinUpdateInProgress"
            (pinChangedEvent)="onPinChanged($event)"
          >
          </app-pro-phone-number-group-item-v2>
        </mat-list-option>
      </cdk-virtual-scroll-viewport>
    </mat-selection-list>
  `,
  styles: [],
  standalone: true,
  imports: [
    MatButtonToggleGroup,
    FormsModule,
    MatButtonToggle,
    MatTooltip,
    MatIcon,
    MatSelectionList,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    MatListOption,
    ProPhoneNumberGroupItemV2Component,
    FlexModule,
    MatDateRangeComponent,
  ],
})
export class ProPhoneNumberGroupV2Component implements OnInit, OnDestroy {
  private readonly TAG = '[ProPhoneNumberGroupV2Component]';
  selectedTheadKey = input<string>('');
  hasContactsModule = input(false);
  hasContactsEditRight = input(false);
  searchText = input<string>('');
  defaultSortType = input<'sortByDateAndUnread' | 'sortByDate' | string>('sortByDateAndUnread');
  sortType = signal<'sortByDateAndUnread' | 'sortByDate' | string>('sortByDateAndUnread');

  selectedMessagingNumber = computed(() => {
    console.log(`${this.TAG} selectedMessagingNumber`, this.messagingService.selectedMessagingNumberSig());
    return this.messagingService.selectedMessagingNumberSig();
  });

  private _onWebSocketConnectSubscription: Subscription;

  pinUpdateInProgress = { threadKey: '', inProgress: false };

  messagingService = inject(ProMessagingService);
  dataSource = inject(ProPhoneNumberGroupV2Datasource);
  authService = inject(AuthService);
  dialog = inject(MatDialog);

  constructor() {
    effect(() => {
      const sortType = this.sortType();
      console.log(`${this.TAG} effect sortType:`, sortType);
      this.dataSource.onSortTypeChanged(sortType);
    });

    effect(() => {
      const threadKey = this.selectedTheadKey();
      console.log(`${this.TAG} effect threadKey:`, threadKey);
      // Defer to the next change detection cycle to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.dataSource.onThreadKeyChanged(threadKey);
      });
    });

    effect(
      () => {
        const phoneNumber = this.selectedMessagingNumber().phoneNumber;
        const searchText = this.searchText();

        console.log(`${this.TAG} effect phoneNumber, searchText:`, phoneNumber, searchText);
        this.filterData();
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit() {
    console.log(`${this.TAG} init`);

    this._onWebSocketConnectSubscription = this.messagingService.onWebSocketConnectSubject.subscribe(() => {
      this.filterData();
    });
  }

  filterData() {
    console.log(`${this.TAG} filterData`);
    this.dataSource.onFilterDataChanged({
      phoneNumber: this.selectedMessagingNumber().phoneNumber,
      searchText: this.searchText(),
      sortType: this.sortType(),
      threadKey: this.selectedTheadKey(),
      startDate: this.messagingService.dateRangeService.rangeStartDate,
      endDate: this.messagingService.dateRangeService.rangeEndDate,
    });
  }

  onRowClick(event, dataItem: MessagingGroupModel) {
    console.log(`${this.TAG} onRowClick`, dataItem);
    this.messagingService.isComposing = false;

    this.markUnreadMessages(dataItem).catch(error => console.error(`${this.TAG} markUnreadMessages`, error));

    this.messagingService.selectedGroup = dataItem;
  }

  async markUnreadMessages(group) {
    console.log(`${this.TAG} mark unread messages`);

    if (group && group.unread > 0) {
      await this.messagingService.markUnreadMessages(group);
    }
  }

  ngOnDestroy() {
    this._onWebSocketConnectSubscription?.unsubscribe();
  }

  sortChanged($event: any) {
    console.log(`${this.TAG} sortChanged $event:`, $event);
    this.authService
      .saveUiSettings({
        smsThreadSortType: $event,
      })
      .then();
    this.dataSource.onSortTypeChanged($event);
  }

  async onPinChanged(dataItem: MessagingGroupModel) {
    const newPinValue = !dataItem.pin;

    if (!this.dataSource) {
      return;
    }

    if (newPinValue) {
      // check pin limits
      if (this.dataSource.totalPinned === 3) {
        this.dialog.open(MatAlertDialogComponent, {
          width: '500px',
          data: {
            title: 'Pin conversation limit reached',
            actionTitle: 'Got It',
            text: `You can pin a maximum of three conversations. To pin this conversation, please unselect one of your currently pinned conversations.`,
          },
        });

        return;
      }
    }

    this.pinUpdateInProgress = {
      threadKey: dataItem.threadKey,
      inProgress: true,
    };

    const result = await this.messagingService.pinConversation(dataItem.threadKey, newPinValue);

    if (result) {
      await this.dataSource.updatePin(dataItem.threadKey, newPinValue);
      this.pinUpdateInProgress = {
        threadKey: dataItem.threadKey,
        inProgress: false,
      };
    }
  }

  protected onDateRangeSelected($event: { start: Date; end: Date }) {
    console.log(`${this.TAG} onDateRangeSelected`, $event);
    this.dataSource.onDateRangeChanged($event.start, $event.end);
  }
}
